import { useMemo, useState } from "react";

export type SortDir = "asc" | "desc";

/**
 * The shared table-sort hook. Every data table in the app uses this
 * instead of a bespoke sortKey/sortDir pair, so click-header-to-sort
 * behaves identically everywhere.
 */
export function useSort<T>(rows: T[], getValue: (row: T, key: string) => unknown, initialKey: string | null = null) {
  const [sortKey, setSortKey] = useState<string | null>(initialKey);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  function toggleSort(key: string) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = getValue(a, sortKey);
      const bv = getValue(b, sortKey);
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv));
    });
    if (sortDir === "desc") copy.reverse();
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggleSort };
}

/** Sort indicator glyph -- ▲/▼ when active, ↕ otherwise. Consistent everywhere. */
export function sortGlyph(key: string, sortKey: string | null, sortDir: SortDir): string {
  if (sortKey !== key) return "↕";
  return sortDir === "asc" ? "▲" : "▼";
}
